<template>
  <Layout>
      <div class="row">
        <div class="col-12">
          <div class="card shadow-sm border-0 rounded-md overflow-hidden">
            <div class="card-body p-3">
              <!-- Header with Logo -->
              <div class="d-flex justify-content-between align-items-center mb-0 pb-2">
                <div class="logo-section">
                  <span v-if="userData.logo!=0">
                    <img :src="getLogo(userData.logo)" height="100px"/>
                  </span>
                  <span v-else>
                    <img src="@/assets/images/logo.png" height="100px"/>
                  </span>
                </div>
                <router-link 
                  :to="{ path: '/entertestresults'}" 
                  type="button" 
                  class="btn btn-outline-primary btn-sm rounded-pill px-3">
                  <i class="mdi mdi-arrow-left me-1"></i> Back
                </router-link>
              </div>

              <!-- Patient Info Cards -->
              <div class="patient-grid mb-2 mt-n4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <div class="info-card p-3 rounded">
                      <div class="d-flex align-items-center">
                        <div>
                          <small class="text-muted d-block">Full Name</small>
                          <strong class="fs-5">{{ patientData.name }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-card p-3 rounded">
                      <div class="d-flex align-items-center">
                        <div>
                          <small class="text-muted d-block">Gender</small>
                          <strong class="fs-5">{{ patientData.gender }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-card p-3 rounded">
                      <div class="d-flex align-items-center">
                        <div>
                          <small class="text-muted d-block">Telephone</small>
                          <strong class="fs-5">0{{ patientData.telephone }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="info-card p-3 rounded">
                      <div class="d-flex align-items-center">
                        <div>
                          <small class="text-muted d-block">Address</small>
                          <strong class="fs-5">{{ patientData.address }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-center mb-2">
                <h4 class="fw-bold mb-1">Laboratory Results</h4>
                <div class="title-underline mx-auto"></div>
              </div>

              <form @submit.prevent="addResults">
                <div v-for="(item, idx) in patientTest" :key="item.id" class="test-item-card mb-2">
                  <div class="card border-0 shadow-sm rounded-lg">
                    <div class="test-header p-3 bg-light rounded-top">
                      <div class="d-flex align-items-center">
                        <span class="test-number me-3">{{ idx + 1 }}</span>
                        <div>
                          <h6 class="mb-0 fw-bold">{{ item.service.name }}</h6>
                          <small class="text-muted">Notes: {{ item.notes }}</small>
                        </div>
                      </div>
                    </div>

                    <!-- Test Body -->
                    <div class="card-body p-2">
                      <div class="row g-3">
                        <!-- Sample Column -->
                        <div class="col-md-2">
                          <label class="form-label fw-semibold mb-2">
                            <i class="mdi mdi-test-tube me-1"></i> Sample Picked
                          </label>
                          <multiselect 
                            v-model="item.sample" 
                            :options="labsamples" 
                            label="name"
                            :searchable="true"
                            placeholder="Select sample"
                            class="modern-multiselect">
                          </multiselect>
                        </div>

                        <!-- Items Used Column -->
                        <div class="col-md-4">
                          <label class="form-label fw-semibold mb-2">
                            <i class="mdi mdi-package-variant me-1"></i> Items Used
                          </label>
                          
                          <!-- Add Product Section -->
                          <div class="add-product-wrapper mb-3">
                            <div class="d-flex gap-2">
                              <multiselect 
                                v-model="item.selected_product" 
                                :options="products" 
                                label="name"
                                track-by="id"
                                :searchable="true"
                                placeholder="Select product"
                                class="modern-multiselect flex-grow-1">
                              </multiselect>
                              <button 
                                type="button" 
                                class="btn btn-success btn-sm px-3 rounded-pill"
                                @click="addProductToTest(item)"
                                :disabled="!item.selected_product">
                                 Add
                              </button>
                            </div>
                          </div>

                          <!-- Selected Items List -->
                          <div v-if="item.items_used && item.items_used.length" class="selected-items-wrapper">
                            <div v-for="(usedItem, uIdx) in item.items_used" :key="uIdx" class="selected-item-card mb-2 p-2 rounded border">
                              <div class="d-flex justify-content-between align-items-start mb-2">
                                <strong class="small">{{ usedItem.name }}</strong>
                                <button 
                                  type="button" 
                                  class="btn btn-link btn-sm text-danger p-0"
                                  @click="removeItemFromTest(item, uIdx)">
                                  <i class="mdi mdi-close-circle"></i>
                                </button>
                              </div>
                              <div class="row g-2">
                                <div class="col-6">
                                  <label class="small text-muted d-block mb-1">Unit</label>
                                  <multiselect 
                                    v-model="usedItem.unit" 
                                    :options="productUnits"
                                    label="unit"
                                    track-by="id"
                                    :searchable="true"
                                    placeholder="Select unit"
                                    @select="updateUnitPrice(usedItem)"
                                    class="modern-multiselect-sm">
                                  </multiselect>
                                </div>
                                <div class="col-6">
                                  <label class="small text-muted d-block mb-1">Quantity</label>
                                  <input 
                                    type="number" 
                                    v-model="usedItem.quantity" 
                                    class="form-control form-control-sm"
                                    min="1"
                                    placeholder="Qty"
                                    @input="updateItemQuantity(item, usedItem)">
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="empty-items text-center py-3 rounded border bg-light">
                            <i class="mdi mdi-package-variant-closed text-muted"></i>
                            <p class="small text-muted mb-0 mt-1">No items added</p>
                          </div>
                        </div>

                        <!-- Results Column -->
                        <div class="col-md-6">
                          <label class="form-label fw-semibold mb-2">
                            <i class="mdi mdi-file-document me-1"></i> Results
                          </label>
                          <tinymce :id="`d`+item.id" v-model="item.result" class="modern-editor"></tinymce>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="form-group d-flex justify-content-end mt-4 pt-3 border-top">
                  <b-button type="submit" class="btn btn-primary px-5 py-2 rounded-pill shadow-sm" variant="primary">
                    <i class="fas fa-check-circle me-2"></i> Submit Results & Items
                  </b-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import Multiselect from "vue-multiselect";
import appConfig from "@/app.config";
import {labsampleMethods, labsampleComputed, productMethods, productComputed} from "@/state/helpers";

export default {
  page: {
    title: "Patient Lab Results",
    meta: [{name: "description",content: appConfig.description,}],
  },
  components: {
    Layout,Multiselect
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      patientData:[],
      patientTest:[{
        service:{name:""}, 
        sample: "",
        result: '',
        items_used: [],
        selected_product: null
      }],
      productUnits: []
    };
  },
  computed: {
    ...labsampleComputed,
    ...productComputed,
    
    totalItemsUsed() {
      let items = [];
      this.patientTest.forEach(test => {
        if (test.items_used && test.items_used.length) {
          items.push(...test.items_used);
        }
      });
      return items;
    },
    
    grandTotal() {
      return this.totalItemsUsed.reduce((total, item) => total + (item.total_price || 0), 0);
    },
    
    itemsSummary() {
      const summary = {};
      this.totalItemsUsed.forEach(item => {
        const name = `${item.name} (${item.unit ? item.unit.unit : 'No Unit'})`;
        const quantity = item.quantity || 1;
        if (summary[name]) {
          summary[name] += quantity;
        } else {
          summary[name] = quantity;
        }
      });
      return summary;
    }
  },
  created() {
    this.showpatientinfo();
    this.fetchLabsamples();
    this.fetchProducts();
  },
  
  methods: {
    ...labsampleMethods,
    ...productMethods,

    getAge(dateString){
      let birth = new Date(dateString); 
      let now = new Date(); 
      let beforeBirth = ((() => {
        birth.setDate(now.getDate());
        birth.setMonth(now.getMonth()); 
        return birth.getTime()
      })() < birth.getTime()) ? 0 : 1;
      return now.getFullYear() - birth.getFullYear() - beforeBirth;
    },
    
    formatCurrency(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    
    getLogo(name){
      let url = axios.defaults.baseURL;
      let imagelink = (url).slice(0,-3)+"logos/"+name;
      return imagelink;
    },
    
    async showpatientinfo(){
      this.visit_id = this.$route.query.id;
      let formData = new FormData();
      formData.append("visit_id", this.visit_id);
      let url = "/findpatientlabinfo";
      await axios.post(url, formData).then((response) => {
        if (response.status == 200) {
          this.patientData = response.data.patient;
          this.showpatienttest();
        }
      })
    },
    
    async showpatienttest(){
      this.visit_id = this.$route.query.id;
      let formData = new FormData();
      formData.append("visit_id", this.visit_id);
      let url = "/findpatientlabtests";
      await axios.post(url, formData).then((response) => {
        if (response.status == 200) {
          this.patientTest = response.data.labtests.map(test => ({
            ...test,
            items_used: test.items_used || [],
            selected_product: null
          }));
        }
      })
    },
    
    async getProductUnits(productId) {
      try {
        let url = `/unitsbyproduct/${productId}`;
        await axios.get(url).then((response) => {
          this.productUnits = response.data.units;
        });
        return this.productUnits || [];
      } catch (error) {
        this.$toast?.error("Error fetching units.");
        return [];
      }
    },
    
    async addProductToTest(test) {
      if (!test.selected_product) return;
      
      const product = test.selected_product;
      const existingItem = test.items_used.find(i => i.id === product.id);
      if (existingItem) {
        this.$toast?.warning(`${product.name} is already added to this test!`);
        return;
      }
      const units = await this.getProductUnits(product.id);
      
      test.items_used.push({
        id: product.id,
        name: product.name,
        unit: units.length > 0 ? units[0] : null,
        base_quantity: product.base_quantity || 0,
        available_units: units
      });
      
      test.selected_product = null;
      test.items_used.sort((a, b) => a.name.localeCompare(b.name));
    },
    
    removeItemFromTest(test, index) {
      test.items_used.splice(index, 1);
    },
    
    updateItemQuantity(test, item) {
      if (item.quantity < 1) {
        item.quantity = 1;
      }
    },
    
    async addResults(){
      let hasErrors = false;
      this.patientTest.forEach(test => {
        if (!test.sample) {
          hasErrors = true;
          this.$toast?.warning(`Please select sample type for ${test.service.name}`) || alert(`Please select sample type for ${test.service.name}`);
        }
        
        test.items_used.forEach(item => {
          if (!item.unit) {
            hasErrors = true;
            this.$toast?.warning(`Please select unit for ${item.name}`) || alert(`Please select unit for ${item.name}`);
          }
        });
      });
      
      if (hasErrors) return;
      
      let obj = {
        results: this.patientTest.map(test => ({
          id: test.id,
          service_id: test.service.id,
          sample: test.sample ? test.sample.id || test.sample : null,
          result: test.result,
          items_used: test.items_used.map(item => ({
            id: item.id,
            name: item.name,
            unit_id: item.unit ? item.unit.id : null,
            unit_name: item.unit ? item.unit.unit : null,
            quantity: item.quantity || 1,
            base_quantity: item.unit ? item.unit.base_quantity || 0 : 0,
          }))
        })),
        visit_id: this.$route.query.id,
        patient_id: this.patientData.patient_id,
        grand_total: this.grandTotal
      };
      
      try {
        let url = "/addpatienttestresults";
        await axios.post(url, obj).then((response) => {
          if (response.status == 200) {
            this.$toast?.success("Results and items saved successfully!");
            this.$router.push('/testresultreports');
          }
        });
      } catch (error) {
        this.$toast?.error("Error saving results. Please try again.");
      }
    }
  },
};
</script>

<style scoped>

/* Patient Info Cards */
.info-card {
  background: transparent;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}

.info-card:hover {
  background: #fff;
  border-color: #dee2e6;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.info-icon {
  width: 48px;
  height: 48px;
  background: rgba(0,123,255,0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Section Title */
.title-underline {
  width: 60px;
  height: 3px;
  background: #007bff;
  border-radius: 3px;
}

/* Test Cards */
.test-item-card {
  transition: all 0.2s ease;
}

.test-item-card .card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.test-item-card .card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important;
}

.test-header {
  border-bottom: 2px solid #007bff;
}

.test-number {
  width: 32px;
  height: 32px;
  background: #007bff;
  color: white;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

/* Form Elements */
.form-label {
  font-size: 0.85rem;
  color: #495057;
}

/* Modern Multiselect Styling */
.modern-multiselect >>> .multiselect__tags {
  border-radius: 8px;
  border-color: #dee2e6;
  min-height: 38px;
}

.modern-multiselect >>> .multiselect__tags:hover {
  border-color: #007bff;
}

.modern-multiselect >>> .multiselect__select {
  height: 36px;
}

.modern-multiselect-sm >>> .multiselect__tags {
  border-radius: 6px;
  min-height: 32px;
  font-size: 13px;
}

.modern-multiselect-sm >>> .multiselect__input {
  font-size: 13px;
}

/* Selected Items */
.selected-items-wrapper {
  max-height: 320px;
  overflow-y: auto;
}

.selected-item-card {
  background: #fff;
  border-color: #e9ecef;
  transition: all 0.2s ease;
}

.selected-item-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.empty-items {
  background: #f8f9fa;
  border-color: #e9ecef;
}

/* Modern Editor */
.modern-editor >>> .tox-tinymce {
  border-radius: 8px;
  border-color: #dee2e6;
}

.modern-editor >>> .tox-tinymce:hover {
  border-color: #007bff;
}

/* Custom Scrollbar */
.selected-items-wrapper::-webkit-scrollbar {
  width: 4px;
}

.selected-items-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.selected-items-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.selected-items-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-lab-view {
    padding: 0.5rem;
  }
  
  .info-card {
    padding: 12px !important;
  }
  
  .test-header {
    padding: 12px !important;
  }
  
  .card-body {
    padding: 5px !important;
  }
}

/* Smooth Transitions */
.btn, .card, .info-card, .selected-item-card {
  transition: all 0.2s ease-in-out;
}

/* Border Radius Utilities */
.rounded-lg {
  border-radius: 10px;
}

.rounded-pill {
  border-radius: 10px;
}
</style>